import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, Loader2, ShieldCheck, Wallet } from "lucide-react";
import CoinIcon from "./CoinIcon";
import { API } from "@/lib/api";
import { formatTRY, formatCrypto } from "@/lib/format";

const DEMO_AMOUNT = 1500;
const COINS = [
  { symbol: "USDT", name: "Tether", network: "TRC20" },
  { symbol: "BTC", name: "Bitcoin", network: "BTC" },
  { symbol: "ETH", name: "Ethereum", network: "ERC20" },
  { symbol: "SOL", name: "Solana", network: "SOL" },
];

// scripted steps
const STEPS = [
  { key: "amount", label: "Tutar giriliyor", dur: 1600 },
  { key: "coin", label: "Kripto para seçiliyor", dur: 1600 },
  { key: "network", label: "Ağ seçiliyor", dur: 1400 },
  { key: "calc", label: "Güncel kripto karşılığı hesaplanıyor", dur: 1600 },
  { key: "address", label: "Ödeme adresi oluşturuluyor", dur: 1600 },
  { key: "waiting", label: "Ödeme bekleniyor", dur: 2200 },
  { key: "detected", label: "Ödeme algılandı", dur: 1500 },
  { key: "confirming", label: "Blockchain doğrulanıyor", dur: 2200 },
  { key: "confirmed", label: "Ödeme onaylandı", dur: 1400 },
  { key: "success", label: "Ödeme başarılı", dur: 2600 },
];

const FAKE_ADDR = "TJ8kR2vQ9xL7mNpZ4wY6cB3aD5eF1gH2iK";
const FAKE_HASH = "0x9f2a...c47e1b";
const PAY_ID = "pay_demo_8f3ka92mx";

export default function LiveDemo() {
  const [idx, setIdx] = useState(0);
  const [crypto, setCrypto] = useState(36.7);
  const coin = COINS[0];
  const timer = useRef();

  useEffect(() => {
    // fetch a realistic quote once
    fetch(`${API}/quote?amount_try=${DEMO_AMOUNT}&coin=USDT`)
      .then((r) => r.json())
      .then((d) => d.crypto_amount && setCrypto(d.crypto_amount))
      .catch(() => {});
  }, []);

  useEffect(() => {
    timer.current = setTimeout(() => setIdx((i) => (i + 1) % STEPS.length), STEPS[idx].dur);
    return () => clearTimeout(timer.current);
  }, [idx]);

  const step = STEPS[idx].key;
  const reached = (k) => STEPS.findIndex((s) => s.key === k) <= idx;
  const qrUrl = `${API}/qr?data=${encodeURIComponent(FAKE_ADDR)}`;

  return (
    <div className="relative w-full max-w-sm mx-auto">
      <div className="absolute -inset-4 rounded-[2rem] bg-primary/10 blur-2xl" aria-hidden />
      <div className="relative rounded-3xl border border-border bg-card shadow-[0_20px_60px_rgba(0,0,0,0.12)] overflow-hidden" data-testid="live-demo-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-primary/10 grid place-items-center">
              <Wallet size={15} strokeWidth={1.5} className="text-primary" />
            </span>
            <span className="text-sm font-semibold">Coinbuy Ödeme</span>
          </div>
          <span className="text-[11px] text-muted-foreground">Demo</span>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Toplam Tutar</p>
            <p className="mt-1 text-3xl font-extrabold tracking-tighter">{formatTRY(DEMO_AMOUNT)}</p>
            <AnimatePresence>
              {reached("calc") && (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1 text-sm font-medium text-primary"
                  data-testid="demo-crypto-amount"
                >
                  ≈ {formatCrypto(crypto, coin.symbol)}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* coin/network chips */}
          <div className="flex items-center justify-center gap-2">
            <motion.div
              animate={{ scale: reached("coin") ? 1 : 0.96, opacity: reached("coin") ? 1 : 0.4 }}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${reached("coin") ? "border-primary/40 bg-primary/5" : "border-border"}`}
            >
              <CoinIcon symbol={coin.symbol} size={20} />
              <span className="text-xs font-semibold">{coin.symbol}</span>
            </motion.div>
            <AnimatePresence>
              {reached("network") && (
                <motion.span
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="rounded-full border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground"
                >
                  {coin.network}
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* body switch */}
          <div className="min-h-[190px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              {!reached("address") && (
                <motion.div key="setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
                  <Loader2 size={28} className="mx-auto animate-spin text-primary" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-muted-foreground">{STEPS[idx].label}...</p>
                </motion.div>
              )}

              {(step === "address" || step === "waiting") && (
                <motion.div key="qr" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="text-center">
                  <div className="mx-auto h-32 w-32 rounded-xl border border-border bg-white p-2">
                    <img src={qrUrl} alt="QR" className="h-full w-full" />
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-muted px-3 py-1.5">
                    <span className="text-xs font-mono text-muted-foreground truncate max-w-[160px]">{FAKE_ADDR}</span>
                    <Copy size={13} className="text-muted-foreground" />
                  </div>
                  {step === "waiting" && (
                    <p className="mt-3 flex items-center justify-center gap-2 text-sm text-amber-500">
                      <span className="relative flex h-2.5 w-2.5"><span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-amber-400" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" /></span>
                      Ödeme bekleniyor...
                    </p>
                  )}
                </motion.div>
              )}

              {step === "detected" && (
                <motion.div key="detected" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 grid place-items-center">
                    <Wallet size={26} strokeWidth={1.5} className="text-primary" />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-primary">Ödeme algılandı</p>
                </motion.div>
              )}

              {step === "confirming" && (
                <motion.div key="confirming" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full text-center px-4">
                  <ShieldCheck size={30} strokeWidth={1.5} className="mx-auto text-primary" />
                  <p className="mt-3 text-sm text-muted-foreground">Blockchain doğrulanıyor...</p>
                  <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <motion.div initial={{ width: "10%" }} animate={{ width: "100%" }} transition={{ duration: 2 }} className="h-full rounded-full bg-primary" />
                  </div>
                </motion.div>
              )}

              {(step === "confirmed" || step === "success") && (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="text-center" data-testid="demo-success">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 18 }} className="mx-auto h-16 w-16 rounded-full bg-emerald-500 grid place-items-center shadow-[0_8px_24px_rgba(16,185,129,0.4)]">
                    <Check size={32} strokeWidth={3} className="text-white" />
                  </motion.div>
                  <p className="mt-3 text-base font-bold">Ödeme Başarılı</p>
                  {step === "success" && (
                    <div className="mt-3 space-y-1 text-left rounded-xl border border-border bg-muted/40 p-3 text-xs">
                      <Row l="Tutar" r={formatTRY(DEMO_AMOUNT)} />
                      <Row l="Kripto" r={formatCrypto(crypto, coin.symbol)} />
                      <Row l="Ödeme No" r={PAY_ID} />
                      <Row l="İşlem Hash" r={FAKE_HASH} />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* progress dots */}
        <div className="flex items-center gap-1 border-t border-border px-5 py-3">
          {STEPS.map((s, i) => (
            <span key={s.key} className={`h-1 flex-1 rounded-full transition-colors ${i <= idx ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ l, r }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{l}</span>
      <span className="font-medium font-mono truncate max-w-[150px]">{r}</span>
    </div>
  );
}
